/**
 * TEMPORARY (2025-06): Token-2022 OKX Routing Gate
 *
 * LiFi doesn't resolve Token-2022 transfer-hook accounts, causing sells to fail
 * with "an account required by the instruction is missing" on instruction #5.
 *
 * This module routes Token-2022 swaps through OKX DEX API (which handles transfer hooks)
 * until LiFi ships hook support. Once LiFi supports Token-2022, delete this entire file
 * and remove the OKX routing branch in use-handle-swap.tsx.
 *
 * Test token (Token-2022 w/ transfer hook): uCF88k62Z4BVq62HGXHcQ12gw7sDX7Wc3XHngwcfrge
 */

import { Connection, PublicKey, VersionedTransaction, Transaction } from "@solana/web3.js";
import bs58 from "bs58";
import { verifyTokenProgram } from "@/lib/burn";
import { SWAP_FEE } from "@/config";

/**
 * Get the authenticated Solana RPC URL for client-side use.
 * Uses /api/rpc proxy which has DRPC credentials on the server.
 * Falls back to public RPC for SSR (which shouldn't happen for swaps).
 */
export function getClientSolanaRpcUrl(): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/rpc?chain=solana`;
  }
  // Fallback for SSR - shouldn't be used for actual swaps
  return "https://api.mainnet-beta.solana.com";
}

/** Fee wallet - same as FORGEPAD_FEE_WALLET used for burn fees */
export const OKX_FEE_WALLET = "4uGpUzreksHAR5mtS6dHEUyKpRANxCsDQBSTHQsnYhut";

/** Platform fee as percentage (1.25% = 1.25) - OKX uses percentage, not bps */
export const PLATFORM_FEE_PERCENT = SWAP_FEE * 100;

/** Platform fee in basis points for logging/display (1.25% = 125 bps) */
export const PLATFORM_FEE_BPS = Math.round(SWAP_FEE * 10000);

/**
 * Get the OKX API base URL.
 * Uses /api/okx proxy which adds credentials server-side.
 * The browser never calls OKX directly or sees the API keys.
 */
function getOkxApiBase(): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/okx`;
  }
  // SSR fallback - shouldn't be used for actual swaps
  return "/api/okx";
}

/** Native SOL mint - not a Token-2022, always routes via LiFi */
const NATIVE_SOL_MINT = "11111111111111111111111111111111";

/** Wrapped SOL mint - classic SPL token, routes via LiFi */
const WSOL_MINT = "So11111111111111111111111111111111111111112";

/** OKX chain index for Solana */
const SOLANA_CHAIN_INDEX = "501";

/**
 * Check if either token in a swap pair is Token-2022.
 * If so, the swap should route through OKX instead of LiFi.
 *
 * TEMPORARY (2025-06): Delete this function when LiFi adds Token-2022 transfer-hook support.
 *
 * @param connection - Solana RPC connection
 * @param inputMint - Input token mint address (string)
 * @param outputMint - Output token mint address (string)
 * @returns true if either token is Token-2022 and swap should use OKX
 */
export async function shouldRouteThroughOkx(
  connection: Connection,
  inputMint: string,
  outputMint: string
): Promise<boolean> {
  console.log(`[OKX ROUTE DECISION] Checking tokens: input=${inputMint}, output=${outputMint}`);

  // Native SOL and wSOL are never Token-2022
  const isInputNative = inputMint === NATIVE_SOL_MINT || inputMint === WSOL_MINT;
  const isOutputNative = outputMint === NATIVE_SOL_MINT || outputMint === WSOL_MINT;
  console.log(`[OKX ROUTE DECISION] isInputNative=${isInputNative}, isOutputNative=${isOutputNative}`);

  let inputIsToken2022 = false;
  let outputIsToken2022 = false;

  // Check input token if not native
  if (!isInputNative) {
    try {
      const inputResult = await verifyTokenProgram(connection, new PublicKey(inputMint));
      console.log(`[OKX ROUTE DECISION] Input token verifyTokenProgram result:`, inputResult);
      if (inputResult === null) {
        console.warn(`[OKX ROUTE DECISION] Input token verification returned null (RPC issue or unknown program)`);
      } else {
        inputIsToken2022 = inputResult.isToken2022;
      }
    } catch (e) {
      console.error(`[OKX ROUTE DECISION] Input token verification THREW ERROR:`, e);
    }
  }

  // Check output token if not native
  if (!isOutputNative) {
    try {
      const outputResult = await verifyTokenProgram(connection, new PublicKey(outputMint));
      console.log(`[OKX ROUTE DECISION] Output token verifyTokenProgram result:`, outputResult);
      if (outputResult === null) {
        console.warn(`[OKX ROUTE DECISION] Output token verification returned null (RPC issue or unknown program)`);
      } else {
        outputIsToken2022 = outputResult.isToken2022;
      }
    } catch (e) {
      console.error(`[OKX ROUTE DECISION] Output token verification THREW ERROR:`, e);
    }
  }

  // Route to OKX only for Token-2022 SELLs (input is Token-2022)
  // - Token-2022 SELL → OKX (works, LiFi fails on transfer hooks)
  // - Token-2022 BUY → LiFi (works, OKX fails with 6049 validation bug)
  const useOkx = inputIsToken2022;
  console.log(`[OKX ROUTE DECISION] inputIsToken2022=${inputIsToken2022}, outputIsToken2022=${outputIsToken2022}, DECISION: ${useOkx ? "OKX" : "LIFI"}`);

  return useOkx;
}

/**
 * OKX quote response structure (subset of fields we use)
 */
export interface OkxQuote {
  chainId: string;
  dexRouterList: Array<{
    router: string;
    routerPercent: string;
    subRouterList: Array<{
      dexProtocol: Array<{
        dexName: string;
        percent: string;
      }>;
      fromToken: {
        tokenContractAddress: string;
        tokenSymbol: string;
        decimal: string;
        tokenAmount: string;
      };
      toToken: {
        tokenContractAddress: string;
        tokenSymbol: string;
        decimal: string;
        tokenAmount: string;
      };
    }>;
  }>;
  fromToken: {
    tokenContractAddress: string;
    tokenSymbol: string;
    decimal: string;
    tokenUnitPrice: string;
  };
  toToken: {
    tokenContractAddress: string;
    tokenSymbol: string;
    decimal: string;
    tokenUnitPrice: string;
  };
  fromTokenAmount: string;
  toTokenAmount: string;
  priceImpactPercentage: string;
  tradeFee: string;
  estimateGasFee: string;
}

/**
 * OKX swap result returned to caller
 */
export interface OkxSwapResult {
  transaction: VersionedTransaction | Transaction;
  quote: OkxQuote;
  outputAmount: string;
  feeMint: string;
}

/**
 * Get an OKX quote for a Token-2022 swap.
 *
 * TEMPORARY (2025-06): Part of Token-2022 routing workaround.
 *
 * @param inputMint - Input token mint
 * @param outputMint - Output token mint
 * @param amount - Amount in smallest units (lamports/base units)
 * @param slippagePercent - Slippage tolerance as decimal (e.g., 0.5 for 0.5%)
 * @param userAddress - User's wallet address (for fee routing)
 * @returns OKX quote response
 */
export async function getOkxQuote(
  inputMint: string,
  outputMint: string,
  amount: string,
  slippagePercent: number,
  userAddress: string
): Promise<OkxQuote> {
  const params = new URLSearchParams({
    chainIndex: SOLANA_CHAIN_INDEX,
    fromTokenAddress: inputMint,
    toTokenAddress: outputMint,
    amount,
    slippagePercent: slippagePercent.toString(),
    feePercent: PLATFORM_FEE_PERCENT.toString(),
    // Take fee from the output token (safer for sells)
    toTokenReferrerWalletAddress: OKX_FEE_WALLET,
  });

  const okxBase = getOkxApiBase();
  console.log(`[OKX PATH] Fetching quote from ${okxBase}/quote`);

  const response = await fetch(`${okxBase}/quote?${params}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`OKX quote failed: ${response.statusText} - ${errorText}`);
  }

  const result = await response.json();

  // OKX wraps response in { code, data, msg }
  if (result.code !== "0") {
    throw new Error(`OKX quote error: ${result.msg || "Unknown error"}`);
  }

  // Return the first route (best quote)
  if (!result.data?.[0]) {
    throw new Error("OKX returned no quote data");
  }

  return result.data[0];
}

/**
 * Build an OKX swap transaction for a Token-2022 swap.
 *
 * TEMPORARY (2025-06): Part of Token-2022 routing workaround.
 *
 * OKX handles Token-2022 transfer hooks automatically.
 *
 * @param userAddress - User's wallet public key
 * @param inputMint - Input token mint
 * @param outputMint - Output token mint
 * @param amount - Amount in smallest units
 * @param slippagePercent - Slippage tolerance as decimal (e.g., 0.5 for 0.5%)
 * @returns Serialized VersionedTransaction ready to sign
 */
export async function buildOkxSwapTransaction(
  userAddress: string,
  inputMint: string,
  outputMint: string,
  amount: string,
  slippagePercent: number
): Promise<OkxSwapResult> {
  const params = new URLSearchParams({
    chainIndex: SOLANA_CHAIN_INDEX,
    fromTokenAddress: inputMint,
    toTokenAddress: outputMint,
    amount,
    slippagePercent: slippagePercent.toString(),
    userWalletAddress: userAddress,
    feePercent: PLATFORM_FEE_PERCENT.toString(),
    // Take fee from the output token
    toTokenReferrerWalletAddress: OKX_FEE_WALLET,
  });

  const okxBase = getOkxApiBase();
  console.log(`[OKX PATH] Building swap via ${okxBase}/swap`);
  console.log(`[OKX PATH] Params:`, {
    fromToken: inputMint.slice(0, 8) + "...",
    toToken: outputMint.slice(0, 8) + "...",
    amount,
    slippage: slippagePercent,
    feePercent: PLATFORM_FEE_PERCENT,
    feeWallet: OKX_FEE_WALLET.slice(0, 8) + "...",
  });

  // OKX swap is a GET request (unlike Jupiter's POST)
  const response = await fetch(`${okxBase}/swap?${params}`);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`OKX swap build failed: ${response.statusText} - ${errorText}`);
  }

  const result = await response.json();

  // OKX wraps response in { code, data, msg }
  if (result.code !== "0") {
    throw new Error(`OKX swap error: ${result.msg || "Unknown error"}`);
  }

  const swapData = result.data?.[0];
  if (!swapData) {
    throw new Error("OKX returned no swap data");
  }

  // Log raw response structure for debugging
  console.log(`[OKX PATH] Raw swap response structure:`, JSON.stringify({
    hasRouterResult: !!swapData.routerResult,
    hasTx: !!swapData.tx,
    txKeys: swapData.tx ? Object.keys(swapData.tx) : [],
    txDataType: swapData.tx?.data ? typeof swapData.tx.data : "undefined",
    txDataLength: swapData.tx?.data ? swapData.tx.data.length : 0,
    txDataPreview: swapData.tx?.data ? swapData.tx.data.slice(0, 50) + "..." : "N/A",
  }, null, 2));

  // OKX returns tx.data as base58-encoded serialized transaction for Solana
  if (!swapData.tx?.data) {
    throw new Error("OKX swap response missing transaction data");
  }

  // Decode from base58 (OKX Solana format)
  const txData = swapData.tx.data;
  let decodedBytes: Uint8Array;

  try {
    decodedBytes = bs58.decode(txData);
    console.log(`[OKX PATH] Decoded ${decodedBytes.length} bytes from base58`);
  } catch (decodeError) {
    console.error(`[OKX PATH] Base58 decode failed, trying base64:`, decodeError);
    // Fallback to base64 if base58 fails
    decodedBytes = new Uint8Array(Buffer.from(txData, "base64"));
    console.log(`[OKX PATH] Decoded ${decodedBytes.length} bytes from base64`);
  }

  // Try VersionedTransaction first (modern Solana), fall back to legacy
  let transaction: VersionedTransaction | Transaction;
  try {
    transaction = VersionedTransaction.deserialize(decodedBytes);
    console.log(`[OKX PATH] Deserialized as VersionedTransaction`);
  } catch (versionedError) {
    console.warn(`[OKX PATH] VersionedTransaction failed, trying legacy Transaction:`, versionedError);
    try {
      transaction = Transaction.from(decodedBytes);
      console.log(`[OKX PATH] Deserialized as legacy Transaction`);
    } catch (legacyError) {
      console.error(`[OKX PATH] Both transaction formats failed:`, { versionedError, legacyError });
      throw new Error(`Failed to deserialize OKX transaction: ${versionedError}`);
    }
  }

  // Build quote info from swap response for logging
  const quote: OkxQuote = {
    chainId: SOLANA_CHAIN_INDEX,
    dexRouterList: swapData.routerResult?.dexRouterList || [],
    fromToken: swapData.routerResult?.fromToken || {
      tokenContractAddress: inputMint,
      tokenSymbol: "",
      decimal: "9",
      tokenUnitPrice: "0",
    },
    toToken: swapData.routerResult?.toToken || {
      tokenContractAddress: outputMint,
      tokenSymbol: "",
      decimal: "9",
      tokenUnitPrice: "0",
    },
    fromTokenAmount: swapData.routerResult?.fromTokenAmount || amount,
    toTokenAmount: swapData.routerResult?.toTokenAmount || "0",
    priceImpactPercentage: swapData.routerResult?.priceImpactPercentage || "0",
    tradeFee: swapData.routerResult?.tradeFee || "0",
    estimateGasFee: swapData.routerResult?.estimateGasFee || "0",
  };

  console.log(`[OKX PATH] Swap transaction built:`, {
    outputAmount: quote.toTokenAmount,
    priceImpact: quote.priceImpactPercentage,
  });

  return {
    transaction,
    quote,
    outputAmount: quote.toTokenAmount,
    // Fee taken from output token
    feeMint: outputMint,
  };
}

/**
 * Execute a complete OKX swap for Token-2022 tokens.
 *
 * TEMPORARY (2025-06): Part of Token-2022 routing workaround.
 *
 * This is the main entry point for Token-2022 swaps. It:
 * 1. Builds the swap transaction with fee configuration
 * 2. Returns the transaction ready for signing
 *
 * @param connection - Solana RPC connection (for token verification)
 * @param userPublicKey - User's wallet public key
 * @param inputMint - Input token mint
 * @param outputMint - Output token mint
 * @param amount - Amount in smallest units
 * @param slippagePercent - Slippage tolerance as decimal (e.g., 0.5 for 0.5%)
 * @returns Transaction and metadata for signing and logging
 */
export async function prepareOkxToken2022Swap(
  connection: Connection,
  userPublicKey: string,
  inputMint: string,
  outputMint: string,
  amount: string,
  slippagePercent: number
): Promise<OkxSwapResult> {
  // Determine which tokens are Token-2022 (for logging)
  const inputResult = await verifyTokenProgram(connection, new PublicKey(inputMint));
  const outputResult = await verifyTokenProgram(connection, new PublicKey(outputMint));

  const inputIsToken2022 = inputResult?.isToken2022 ?? false;
  const outputIsToken2022 = outputResult?.isToken2022 ?? false;

  console.log(`[OKX PATH] Preparing swap:`, {
    inputMint: inputMint.slice(0, 8) + "...",
    outputMint: outputMint.slice(0, 8) + "...",
    inputIsToken2022,
    outputIsToken2022,
    amount,
    slippagePercent,
  });

  // Build and return transaction
  const result = await buildOkxSwapTransaction(
    userPublicKey,
    inputMint,
    outputMint,
    amount,
    slippagePercent
  );

  console.log(`[OKX PATH] Swap prepared:`, {
    outputAmount: result.outputAmount,
    feeMint: result.feeMint.slice(0, 8) + "...",
  });

  return result;
}
